import React, { FC } from "react";
import { Dropdown, NumberInput, InputsGroup, IOption } from "@evergis/ui";
import { useTranslation } from "react-i18next";

import { useLayerAttributeOptions } from "../../../../../../hooks/attributes/useLayerAttributeOptions";
import { TemplateFormState } from "../../../hooks/useTemplateForm";
import { getNumberAttributesList } from "../../../hooks/useCurrentAttributesList";
import { GeometryTypeInfo } from "./TemplateForm";
import { useGeometryPropertiesOptions } from "../../../hooks/useGeometryPropertiesOptions";

export enum TemplateType {
  AttrValue = "attrValue",
  AttrAttr = "attrAttr",
  Geometry = "geometry",
  Attribute = "attribute",
}

type TemplateFormActionsProps = {
  templateType: TemplateType;
  layerName: string;
  templateFormState: TemplateFormState;
  geometryTypeInfo: GeometryTypeInfo;
  onChangeValue: (value: number) => void;
  onChangeAttribute: (parameterName: string) => (option: IOption[]) => void;
};

export const actionsList = [
  { text: "+", value: "sum" },
  { text: "-", value: "subtract" },
  { text: "*", value: "multi" },
  { text: "/", value: "div" },
];

export const TemplateFormActions: FC<TemplateFormActionsProps> = ({
  templateType,
  layerName,
  geometryTypeInfo,
  templateFormState,
  onChangeValue,
  onChangeAttribute,
}) => {
  const { t } = useTranslation("dialogs");
  const attributeOptions = useLayerAttributeOptions(layerName);
  const { attrValue, attrAttr, geometry, attribute } = templateFormState;
  const numberAttributeOptions = getNumberAttributesList(attributeOptions);
  const geometryOptions = useGeometryPropertiesOptions(geometryTypeInfo);

  switch (templateType) {
    case TemplateType.AttrValue:
      return (
        <>
          <Dropdown
            label={t("featuresMultiEdit.attribute")}
            width={"18rem"}
            options={numberAttributeOptions}
            zIndex={10000}
            onChange={onChangeAttribute("option")}
            value={attrValue && attrValue.option && attrValue.option.value}
          />
          <InputsGroup>
            <Dropdown
              label={t("featuresMultiEdit.action")}
              width={"7.25rem"}
              options={actionsList}
              zIndex={10000}
              onChange={onChangeAttribute("action")}
              value={attrValue && attrValue.action && attrValue.action.value}
            />

            <NumberInput
              label={t("featuresMultiEdit.value")}
              width={"10.75rem"}
              onChange={onChangeValue}
              value={(attrValue && attrValue.value) || null}
            />
          </InputsGroup>
        </>
      );
    case TemplateType.AttrAttr:
      return (
        <>
          <Dropdown
            label={t("featuresMultiEdit.attributeNo1")}
            width={"18rem"}
            options={numberAttributeOptions}
            zIndex={10000}
            onChange={onChangeAttribute("option1")}
            value={attrAttr && attrAttr.option1 && attrAttr.option1.value}
          />
          <InputsGroup>
            <Dropdown
              label={t("featuresMultiEdit.action")}
              width={"4.75rem"}
              options={actionsList}
              zIndex={10000}
              onChange={onChangeAttribute("action")}
              value={attrAttr && attrAttr.action && attrAttr.action.value}
            />
            <Dropdown
              label={t("featuresMultiEdit.attributeNo2")}
              width={"13.25rem"}
              options={numberAttributeOptions}
              zIndex={10000}
              onChange={onChangeAttribute("option2")}
              value={attrAttr && attrAttr.option2 && attrAttr.option2.value}
            />
          </InputsGroup>
        </>
      );
    case TemplateType.Geometry:
      return (
        <Dropdown
          label={t("featuresMultiEdit.geometry")}
          width={"18rem"}
          options={geometryOptions}
          zIndex={10000}
          onChange={onChangeAttribute("option")}
          value={geometry && geometry.option && geometry.option.value}
        />
      );
    case TemplateType.Attribute:
      return (
        <Dropdown
          label={t("featuresMultiEdit.attribute")}
          width={"18rem"}
          options={attributeOptions}
          zIndex={10000}
          onChange={onChangeAttribute("option")}
          value={attribute && attribute.option && attribute.option.value}
        />
      );
    default:
      return null;
  }
};
